import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { BookOpen, ChevronRight, Folder, FolderOpen, Library, ListTree, Network, Plus, Search, Settings2, Sparkles, Upload } from 'lucide-react'
import { getKnowledgeBase, uploadFile } from '../../api/kb'
import type { KnowledgeBase } from '../../api/types'
import { KbCreateDialog, KbSettingsDialog } from './KbDialogs'
import { IconButton, Pill } from '../../ui'
import { useKnowledgeBases } from '../chat/Composer'
import { shortDate } from './DocCard'
import { DocList } from './DocList'
import { DocReader, useDoc } from './DocReader'
import { KbAssistant } from './KbAssistant'
import { KbTree, type FolderSel } from './KbTree'
import './kb.css'
import './workspace.css'

export function KbListPage() {
  const kbs = useKnowledgeBases()
  const [q, setQ] = useState('')
  const [creating, setCreating] = useState(false)
  const list = (kbs.data ?? []).filter((kb) => !q.trim() || kb.name.includes(q.trim()) || kb.description?.includes(q.trim()))
  return (
    <div className="page">
      <div className="page-h">
        <h1>知识库</h1>
        <span className="grow" />
        <label className="sk-search" style={{ width: 260 }}>
          <Search width={16} height={16} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="搜索知识库" aria-label="搜索知识库" />
        </label>
        <Pill variant="primary" icon={<Plus />} onClick={() => setCreating(true)} disabled={!kbs.data}>
          新建知识库
        </Pill>
      </div>
      {creating && <KbCreateDialog kbs={kbs.data ?? []} onClose={() => setCreating(false)} />}
      {kbs.isError && <p className="empty">没能载入知识库：{kbs.error.message}</p>}
      {kbs.isPending && <div className="kb-grid" aria-busy="true" />}
      {kbs.data && list.length === 0 && <p className="empty">{q ? '没有匹配的知识库' : '还没有知识库'}</p>}
      <div className="kb-grid">
        {list.map((kb) => (
          <Link key={kb.id} to={`/kb/${kb.id}`} className="sk-card kb-card" data-interactive="true" style={{ textDecoration: 'none', color: 'inherit' }}>
            <span className="kb-cover" aria-hidden="true">
              <Library />
            </span>
            <span className="sk-card-title">{kb.name}</span>
            {kb.description && <span className="sk-card-body">{kb.description}</span>}
            <span className="sk-card-meta">
              <span className="sk-chip">{kb.knowledge_count ?? 0} 篇</span>
              {kb.indexing_strategy?.wiki_enabled && <span className="sk-chip">Wiki</span>}
              {kb.indexing_strategy?.graph_enabled && <span className="sk-chip">关联图</span>}
              {kb.is_processing && <span className="sk-chip">{kb.processing_count ?? ''} 篇在解析</span>}
              <span style={{ flex: 1 }} />
              {kb.updated_at && shortDate(kb.updated_at)}
            </span>
          </Link>
        ))}
      </div>
    </div>
  )
}

interface UploadItem {
  id: string
  name: string
  progress: number
  state: 'uploading' | 'done' | 'duplicate' | 'error'
  error?: string
}

/**
 * 知识库：左边分区、中间阅读（没打开资料时是列表）、右边 AI 侧栏。
 * /kb/:kbId 中间是列表（?folder= 某个分区的全部，?q= 搜索）；/kb/:kbId/d/:docId 中间是这一篇。
 * 别处带来的 ?doc=（站内双链、旧地址）在这里直接换成阅读页。
 */
export function KbPage() {
  const { kbId = '', docId } = useParams()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const qc = useQueryClient()
  const kb = useQuery({ queryKey: ['knowledge-base', kbId], queryFn: () => getKnowledgeBase(kbId) })
  const doc = useDoc(docId)
  const folderParam = params.get('folder')
  const folder: FolderSel = folderParam === null ? undefined : folderParam
  const keyword = params.get('q')?.trim() ?? ''
  const [quote, setQuote] = useState<string | null>(null)
  const [settings, setSettings] = useState(false)
  // 手机上：中间只放正文（或列表），分区和 AI 从底下拉出来
  const [sheet, setSheet] = useState<'tree' | 'ai' | null>(null)
  useEffect(() => setSheet(null), [docId, folderParam, keyword])
  const [uploads, setUploads] = useState<UploadItem[]>([])
  const fileInput = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const d = params.get('doc')
    if (d) navigate(`/kb/${kbId}/d/${d}`, { replace: true })
  }, [params, kbId])
  useEffect(() => setQuote(null), [docId])

  const startUpload = async (files: FileList | null) => {
    if (!files?.length) return
    const folderPath = folder || undefined
    for (const file of Array.from(files)) {
      const id = `${file.name}-${Date.now()}-${Math.random()}`
      setUploads((u) => [...u, { id, name: file.name, progress: 0, state: 'uploading' }])
      const patch = (p: Partial<UploadItem>) => setUploads((u) => u.map((x) => (x.id === id ? { ...x, ...p } : x)))
      try {
        const res = await uploadFile(kbId, file, { folderPath, onProgress: (progress) => patch({ progress }) })
        patch({ progress: 1, state: res.duplicate ? 'duplicate' : 'done' })
        setTimeout(() => setUploads((u) => u.filter((x) => x.id !== id)), res.duplicate ? 6000 : 2500)
      } catch (e) {
        patch({ state: 'error', error: e instanceof Error ? e.message : '没传上去' })
      }
    }
    qc.invalidateQueries({ queryKey: ['knowledge', kbId] })
    qc.invalidateQueries({ queryKey: ['folders', kbId] })
  }

  if (kb.isError) return <p className="empty" style={{ padding: 40 }}>找不到这个知识库</p>

  return (
    <div className="kbw" data-sheet={sheet ?? undefined}>
      <KbTree kbId={kbId} kb={kb.data} doc={doc.data} folder={folder} keyword={keyword} onUpload={() => fileInput.current?.click()} onSettings={() => setSettings(true)} onClose={() => setSheet(null)} />
      <input ref={fileInput} type="file" multiple hidden onChange={(e) => { void startUpload(e.target.files); e.target.value = '' }} aria-label="选择要上传的文件" />
      {settings && kb.data && <KbSettingsDialog kb={kb.data} onClose={() => setSettings(false)} />}
      <main className="kbw-main">
        {docId ? (
          <DocReader
            docId={docId}
            onAsk={(q) => {
              setQuote(q)
              if (matchMedia('(max-width: 900px)').matches) setSheet('ai')
            }}
          />
        ) : (
          <DocList kbId={kbId} folder={folder} keyword={keyword} />
        )}
        <div className="kbw-bar" role="toolbar" aria-label="知识库工具">
          <button type="button" aria-label="打开分区" onClick={() => setSheet('tree')}>
            <ListTree aria-hidden="true" />
            {kb.data?.name ?? '分区'}
          </button>
          <button type="button" aria-label="打开 AI 侧栏" onClick={() => setSheet('ai')}>
            <Sparkles aria-hidden="true" />
            问 AI
          </button>
        </div>
        {uploads.length > 0 && (
          <div className="upload-list kbw-uploads" aria-live="polite">
            {uploads.map((u) => (
              <div key={u.id} className="upload-item" data-state={u.state}>
                <Upload width={14} height={14} />
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name}</span>
                {u.state === 'uploading' && (
                  <span className="bar" role="progressbar" aria-valuenow={Math.round(u.progress * 100)} aria-valuemin={0} aria-valuemax={100} aria-label={`${u.name} 上传进度`}>
                    <i style={{ width: `${u.progress * 100}%` }} />
                  </span>
                )}
                {u.state === 'done' && <span>已上传，正在解析</span>}
                {u.state === 'duplicate' && <span>已经有这个文件了</span>}
                {u.state === 'error' && (
                  <>
                    <span>{u.error}</span>
                    <IconButton label="关闭" onClick={() => setUploads((x) => x.filter((y) => y.id !== u.id))}>
                      ×
                    </IconButton>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
      <KbAssistant kb={kb.data} doc={docId ? doc.data : undefined} quote={quote} onClearQuote={() => setQuote(null)} onClose={() => setSheet(null)} />
      {sheet && <div className="kbw-scrim" onClick={() => setSheet(null)} aria-hidden="true" />}
    </div>
  )
}

/** 知识库页的头：面包屑、库名、页签（原文资料 / Wiki）、右边的按钮、知识库设置。 */
export function KbHead({ kbId, kb, missing, tab, children }: { kbId: string; kb?: KnowledgeBase; missing?: boolean; tab: 'docs' | 'wiki'; children?: React.ReactNode }) {
  const [settings, setSettings] = useState(false)
  return (
    <div className="kbp-head">
      <div>
        <div className="crumb">
          <Link to="/kb">知识库</Link>
          <ChevronRight width={13} height={13} />
          <span>{kb?.name ?? ''}</span>
        </div>
        <h1>{kb?.name ?? (missing ? '找不到这个知识库' : '')}</h1>
      </div>
      <nav className="kb-tabs" aria-label="知识库内容">
        <Link className="sk-pill" data-size="sm" data-variant={tab === 'docs' ? 'selected' : 'ghost'} aria-current={tab === 'docs' ? 'page' : undefined} to={`/kb/${kbId}`}>
          <BookOpen width={14} height={14} />
          原文资料
        </Link>
        {kb?.indexing_strategy?.wiki_enabled && (
          <Link className="sk-pill" data-size="sm" data-variant={tab === 'wiki' ? 'selected' : 'ghost'} aria-current={tab === 'wiki' ? 'page' : undefined} to={`/kb/${kbId}/wiki`}>
            <Network width={14} height={14} />
            Wiki
          </Link>
        )}
      </nav>
      <span className="grow" />
      {children}
      {kb && (
        <IconButton label="知识库设置" onClick={() => setSettings(true)}>
          <Settings2 />
        </IconButton>
      )}
      {settings && kb && <KbSettingsDialog kb={kb} onClose={() => setSettings(false)} />}
    </div>
  )
}

export function FolderRow({ name, count, current, onClick, depth = 0, expanded, onToggle }: { name: string; count?: number; current: boolean; onClick: () => void; depth?: number; expanded?: boolean; onToggle?: () => void }) {
  return (
    <div className="tree-row" data-current={current} style={{ paddingLeft: 4 + depth * 16 }}>
      {onToggle ? (
        <button type="button" className="tree-twisty" aria-expanded={expanded} aria-label={`${expanded ? '收起' : '展开'}「${name}」`} onClick={onToggle}>
          <ChevronRight className="twisty" data-open={expanded} />
        </button>
      ) : (
        <span className="tree-twisty" aria-hidden="true" />
      )}
      <button type="button" className="tree-pick" aria-current={current} onClick={onClick}>
        {current ? <FolderOpen /> : <Folder />}
        <span className="name">{name}</span>
        {count !== undefined && <span className="n">{count}</span>}
      </button>
    </div>
  )
}
